import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  pdfUrl: string;
  isOpen: boolean;
  onClose: () => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl, isOpen, onClose }) => {
  const VIEWER_WIDTH = 500;
  const VIEWER_HEIGHT = 600;

  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [position, setPosition] = useState({
    x: window.innerWidth / 2 - VIEWER_WIDTH / 2,
    y: window.innerHeight / 2 - VIEWER_HEIGHT / 2,
  });
  const [size, setSize] = useState({
    width: VIEWER_WIDTH,
    height: VIEWER_HEIGHT,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStartPosition, setResizeStartPosition] = useState({
    x: 0,
    y: 0,
  });
  const [resizeStartSize, setResizeStartSize] = useState({
    width: 0,
    height: 0,
  });
  const modalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track visible page based on scroll position
  useEffect(() => {
    if (!containerRef.current || !numPages) return;

    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerMiddle = containerRect.top + containerRect.height / 2;

      // Find the page that is most visible in the viewport
      let closestPage = 1;
      let closestDistance = Number.MAX_VALUE;

      for (let i = 1; i <= numPages; i++) {
        const pageElement = document.getElementById(`pdf-page-${i}`);
        if (pageElement) {
          const pageRect = pageElement.getBoundingClientRect();
          const pageMiddle = pageRect.top + pageRect.height / 2;
          const distance = Math.abs(containerMiddle - pageMiddle);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestPage = i;
          }
        }
      }

      if (closestPage !== pageNumber) {
        setPageNumber(closestPage);
      }
    };

    const container = containerRef.current;
    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [numPages, pageNumber]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  function changePage(offset: number) {
    setPageNumber((prevPageNumber) => {
      const newPageNumber = prevPageNumber + offset;
      const validPageNumber = Math.max(
        1,
        Math.min(numPages || 1, newPageNumber)
      );

      // Scroll to the page
      const pageElement = document.getElementById(
        `pdf-page-${validPageNumber}`
      );
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: "smooth" });
      }

      return validPageNumber;
    });
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  function zoomIn() {
    setScale((prevScale) => Math.min(prevScale + 0.1, 2.5));
  }

  function zoomOut() {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));
  }

  function fitToWindow() {
    if (containerRef.current && numPages) {
      const containerWidth = containerRef.current.clientWidth - 40; // Accounting for padding
      setScale(containerWidth / 595); // 595 is a standard width for A4 in PDF points
    }
  }

  // Reset size when modal is opened
  useEffect(() => {
    if (isOpen) {
      setSize({ width: VIEWER_WIDTH, height: VIEWER_HEIGHT });
    }
  }, [isOpen]);

  // Reset position when modal is opened
  useEffect(() => {
    if (isOpen) {
      setPosition({
        x: window.innerWidth / 2 - VIEWER_WIDTH / 2,
        y: window.innerHeight / 2 - VIEWER_HEIGHT / 2,
      });
    }
  }, [isOpen]);

  // Custom drag handlers - Only allow dragging from the header
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (
        modalRef.current &&
        e.target instanceof Element &&
        e.target.closest(".pdf-viewer-handle h3")
      ) {
        setIsDragging(true);
        setDragOffset({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
        e.preventDefault();
      }
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
        e.preventDefault();
      } else if (isResizing) {
        const newWidth = Math.max(
          VIEWER_WIDTH / 2,
          resizeStartSize.width + (e.clientX - resizeStartPosition.x)
        );
        const newHeight = Math.max(
          VIEWER_HEIGHT / 2,
          resizeStartSize.height + (e.clientY - resizeStartPosition.y)
        );
        setSize({ width: newWidth, height: newHeight });
        e.preventDefault();
      }
    },
    [isDragging, isResizing, dragOffset, resizeStartPosition, resizeStartSize]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (modalRef.current) {
        setIsResizing(true);
        setResizeStartPosition({ x: e.clientX, y: e.clientY });
        setResizeStartSize({ width: size.width, height: size.height });
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [size]
  );

  // Add and remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isOpen, handleMouseMove, handleMouseUp]);

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      onMouseDown={handleMouseDown}
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        minWidth: `${VIEWER_WIDTH / 2}px`,
        minHeight: `${VIEWER_HEIGHT / 2}px`,
        zIndex: 1000,
        resize: "both",
        overflow: "hidden",
      }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-xl"
    >
      <div
        className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize"
        onMouseDown={handleResizeStart}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="text-gray-400"
        >
          <path d="M22 22H16V16H22V22ZM22 13H13V22H22V13Z" opacity="0.3" />
          <path d="M2 2H11V11H2V2ZM11 11H13V13H11V11ZM13 13H15V15H13V13ZM15 15H17V17H15V15ZM17 17H19V19H17V17ZM19 19H21V21H19V19Z" />
        </svg>
      </div>
      <div className="flex flex-col h-full">
        {/* Header with controls */}
        <div className="pdf-viewer-handle flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 w-full cursor-move">
            PDF Viewer
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={zoomOut}
              className="p-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              title="Zoom Out"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 12H4"
                />
              </svg>
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              className="p-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              title="Zoom In"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
            <button
              onClick={fitToWindow}
              className="p-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              title="Fit to Window"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              title="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* PDF Document */}
        <div className="flex-1 overflow-auto p-4" ref={containerRef}>
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error("Error loading PDF:", error);
            }}
            loading={
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            }
            error={
              <div className="flex items-center justify-center h-full text-red-500">
                Failed to load PDF. Please check the URL and try again.
              </div>
            }
          >
            {Array.from(new Array(numPages || 0), (_, index) => (
              <div
                id={`pdf-page-${index + 1}`}
                key={`page_${index + 1}`}
                className="mb-4"
              >
                <Page pageNumber={index + 1} scale={scale} renderTextLayer={true} />
              </div>
            ))}
          </Document>
        </div>

        {/* Footer with pagination */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-700">
          <button
            onClick={previousPage}
            disabled={pageNumber <= 1}
            className={`px-3 py-1 rounded ${
              pageNumber <= 1
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-600"
            }`}
          >
            Previous
          </button>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Page {pageNumber} of {numPages || "--"}
          </p>
          <button
            onClick={nextPage}
            disabled={pageNumber >= (numPages || 1)}
            className={`px-3 py-1 rounded ${
              pageNumber >= (numPages || 1)
                ? "text-gray-400 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-600"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
